import { isDate, isError } from "lodash";
import { isAxiosError } from "@/api/api";
import { filterText, isSecretParam, REDACTED } from "./filterDomain";
import { RequestErrorData, toRequestErrorData } from "./requestErrorDomain";

const MAX_DEPTH = 4;
const MAX_ENTRIES = 20;
const MAX_STRING_LENGTH = 1000;
const MAX_STACK_LENGTH = 10_000;

export type SerializedError = {
  name: string;
  message: string;
  stack?: string;
  request?: RequestErrorData;
};

export function serializeError(error: Error): SerializedError {
  return {
    name: error.name,
    message: filterText(error.message.slice(0, MAX_STRING_LENGTH)),
    stack: error.stack && filterText(error.stack.slice(0, MAX_STACK_LENGTH)),
    ...(isAxiosError(error) ? { request: toRequestErrorData(error) } : {}),
  };
}

export function serializeValue(value: unknown, depth = MAX_DEPTH): unknown {
  switch (true) {
    case value === null || value === undefined:
    case typeof value === "boolean" || typeof value === "number":
      return value;
    case typeof value === "string":
      return filterText(value.slice(0, MAX_STRING_LENGTH));
    case typeof value === "bigint" || typeof value === "symbol":
      return String(value);
    case typeof value === "function":
      return `[function ${value.name}]`;
    case isError(value):
      return serializeError(value);
    case isDate(value):
      return value.toISOString();
    case Array.isArray(value):
      return depth === 0
        ? "[Array]"
        : value.slice(0, MAX_ENTRIES).map((item) => serializeValue(item, depth - 1));
    case typeof value === "object":
      return depth === 0 ? "[Object]" : serializeEntries(value, depth);
    default:
      return String(value);
  }
}

function serializeEntries(value: object, depth: number): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(value)
      .slice(0, MAX_ENTRIES)
      .map(([key, entry]) => [
        key,
        isSecretParam(key) ? REDACTED : serializeValue(entry, depth - 1),
      ]),
  );
}
